import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import io from "socket.io-client";
import { attachVendorOrderRooms } from "../hooks/useVendorOrderRooms";
import { useBranchScope } from "../context/BranchScopeContext";
import { useVendorAccess } from "../hooks/useVendorAccess";
import { vendorOwnerIdOf } from "../utilities/vendorScope";
import socketAuthOptions from "../utilities/socketAuth";
import { useHistory } from "react-router-dom";
import { cardLink, cardLinkProps } from "../utilities/orderDeepLinks";
import { formatAvgDelivery } from "../utilities/deliveryTime";
import { fetchSellerOrderStats } from "../actions/sellerStatsActions";
import { apiFetchBrandStats, apiFetchBrandOwnerStats, apiGetVendorShopPath, apiGetVendorAnalytics, apiGetVendorRealtimeAnalytics } from "../api/index.js";
import axios from "axios";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  makeStyles,
  Button,
} from "@material-ui/core";
import {
  Refresh as RefreshIcon,
  Notifications as NotificationsIcon,
  NotificationsOff as NotificationsOffIcon,
  Payment as PaymentIcon,
  LocalShipping as LocalShippingIcon,
  Timer as TimerIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import useOrderAlerts from "../hooks/useOrderAlerts";

import newOrderAudioNotification from "../assets/img/restaurant_notification_beep.mp3";
import { getUserFromLocalStorage } from "../utilities/user.js";
import { getTokenFromLocalStorage } from "../utilities/tokenUtils.js";
import ChatAnalytics from "../components/ChatAnalytics.js";
import VendorSalesAnalytics from "../components/VendorSalesAnalytics";
import VendorEarnings from "../components/money/VendorEarnings";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(2),
    backgroundColor: "#f0f2f5",
    minHeight: "100vh",
    overflow: "auto",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    position: "relative",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    overflow: "hidden",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "3px",
      background:
        "linear-gradient(90deg, rgba(255,255,255,0.6), rgba(255,255,255,0.9), rgba(255,255,255,0.6))",
    },
  },
  cardContent: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: theme.spacing(2),
    position: "relative",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  cardFooter: {
    marginTop: "auto",
    padding: theme.spacing(1),
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    textAlign: "center",
    backdropFilter: "blur(10px)",
    fontWeight: 600,
    fontSize: "0.75rem",
    letterSpacing: "0.3px",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.1)",
    },
  },
  newOrdersFooter: {
    color: "#eeb8b3ff",
    cursor: "pointer",
  },
  pendingDeliveryFooter: {
    color: "#eedcaaff",
  },
  deliveredFooter: {
    color: "#1FA62B",
  },
  assignedOrdersFooter: {
    color: "#e5dac5ff",
  },
  avgDeliveryTimeFooter: {
    color: "#d3d8d8ff",
  },
  paymentMethodsFooter: {
    color: "#fff5daff",
  },
  notificationFooter: {
    color: "#c6d3bbff",
  },
  icon: {
    fontSize: 36,
    opacity: 1,
    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
  },
  error: {
    color: theme.palette.error.main,
    textAlign: "center",
    marginBottom: theme.spacing(2),
  },
  notificationButton: {
    backgroundColor: "#ffffff",
    color: "#4caf50",
    borderRadius: "10px",
    padding: theme.spacing(1, 2.5),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.9rem",
    boxShadow: "0 3px 10px rgba(76, 175, 80, 0.3)",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#4caf50",
      color: "#ffffff",
      transform: "translateY(-2px)",
      boxShadow: "0 6px 16px rgba(76, 175, 80, 0.4)",
    },
  },
  notificationButtonOff: {
    backgroundColor: "#ffffff",
    color: "#f44336",
    borderRadius: "10px",
    padding: theme.spacing(1, 2.5),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.9rem",
    boxShadow: "0 3px 10px rgba(244, 67, 54, 0.3)",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#f44336",
      color: "#ffffff",
      transform: "translateY(-2px)",
      boxShadow: "0 6px 16px rgba(244, 67, 54, 0.4)",
    },
  },
  dashboardTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: "#1a1a1a",
    fontSize: "1.75rem",
    letterSpacing: "-0.5px",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  statNumber: {
    fontWeight: 700,
    textShadow: "0 2px 8px rgba(0,0,0,0.4)",
    marginTop: theme.spacing(0.5),
    color: "#ffffff !important",
  },
  cardTitle: {
    fontWeight: 600,
    letterSpacing: "0.5px",
    fontSize: "0.875rem",
    color: "#ffffff !important",
    textShadow: "0 1px 3px rgba(0,0,0,0.3)",
  },
  paymentMethod: {
    display: "inline-block",
    padding: theme.spacing(0.5, 1.5),
    margin: theme.spacing(0.5),
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    borderRadius: "20px",
    fontSize: "0.875rem",
    fontWeight: 600,
    color: "#ffffff",
    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
    backdropFilter: "blur(5px)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
  },
}));

const SellerDashboard = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { stats: rawStats, loading, error } = useSelector((state) => state.sellerStats);
  const stats = rawStats || {};

  const user = getUserFromLocalStorage();
  const isBrandOwner = user?.result?.operationMode === "BRAND_OWNER";
  // Every order card is a link to this vendor's orders list (Orders & Quotes
  // for a brand, Shop Orders otherwise), opened on the filter the card's
  // count was made with (utilities/orderDeepLinks.js), keyboard included.
  const ordersAudience = isBrandOwner ? "brand" : "retailer";
  const openOrders = (card, label) => cardLinkProps(() => history.push(cardLink(ordersAudience, card)), label);

  const [vendorId, setVendorId] = useState(null);
  const [brandId, setBrandId] = useState(null);
  const [brandName, setBrandName] = useState("");
  /* Every brand this owner holds. The stats cards above still follow the first
     brand, but earnings are PER BRAND: each brand is its own commercial panel
     with its own commission contract, so merging them hides the one that is
     losing money. */
  const [brands, setBrands] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);

  // ── Brand SEO analytics state ──────────────────────────────────────────────
  const [brandShopPath, setBrandShopPath] = useState(null);
  const [brandAnalytics, setBrandAnalytics] = useState(null);
  const [brandAnalyticsLoading, setBrandAnalyticsLoading] = useState(false);
  const [brandAnalyticsError, setBrandAnalyticsError] = useState(null);
  const [brandAnalyticsDays, setBrandAnalyticsDays] = useState(30);

  // ── Retailer / Wholesaler GA4 analytics state ──────────────────────────────
  const isRetailerWholesaler = !isBrandOwner;
  const [retailerShopPath, setRetailerShopPath] = useState(null);
  const [retailerAnalytics, setRetailerAnalytics] = useState(null);
  const [retailerAnalyticsLoading, setRetailerAnalyticsLoading] = useState(false);
  const [retailerAnalyticsDays, setRetailerAnalyticsDays] = useState(30);
  const [retailerAnalyticsTab, setRetailerAnalyticsTab] = useState('overview'); // 'overview' | 'realtime'
  const [retailerRealtimeData, setRetailerRealtimeData] = useState(null);
  const [retailerRealtimeLoading, setRetailerRealtimeLoading] = useState(false);
  const [retailerRealtimeLastUpdated, setRetailerRealtimeLastUpdated] = useState(null);

  // Shared, reliable alert engine (audio unlock + hidden-tab desktop notice + Web
  // Push) — replaces this file's bespoke AudioContext + useSound + hasInteracted.
  const alerts = useOrderAlerts({ soundUrl: newOrderAudioNotification });
  const notifyRef = useRef(alerts.notify);
  notifyRef.current = alerts.notify;
  // The browser can refuse to play the alert tone (autoplay policy) even when
  // the toggle below says On. That used to fail completely silently — surfaced
  // now so the vendor is told rather than just never hearing an order.
  const audioBlocked = alerts.audioBlocked;
  // On/Off reflects the ENGINE's persisted state (single source of truth), so the
  // card can't say "On" while the engine is off, and toggling on enables + plays.
  const notificationsEnabled = alerts.enabled;
  const notificationsEnabledRef = useRef(notificationsEnabled);
  notificationsEnabledRef.current = notificationsEnabled;

  // Branches (4 Oct 2026): the stats follow the branch picked in the navbar;
  // earnings and quotes are head office's.
  const { outletIdParam } = useBranchScope();
  const { isBranchScoped } = useVendorAccess();
  const statsOpts = { outletId: outletIdParam };

  useEffect(() => {
    if (user) {
      // The owner's id, also for a team member.
      setVendorId(vendorOwnerIdOf(user));
    }
  }, []);

  const fetchData = () => {
    if (isBrandOwner) {
      apiFetchBrandOwnerStats(statsOpts)
        .then(({ data }) => {
          if (data?.success) {
            dispatch({
              type: "FETCH_SELLER_STATS_SUCCESS",
              payload: {
                newOrders: data.data.newOrders,
                pendingDelivery: data.data.pendingDelivery,
                delivered: data.data.delivered,
                assigned: data.data.openQuoteRequests,
                paymentMethods: data.data.paymentMethods || {},
                avgDeliveryMinutes: data.data.avgDeliveryMinutes || 0,
              },
            });
          }
        })
        .catch((e) => console.error("Brand owner stats error", e))
        .finally(() => setInitialLoading(false));
    } else if (!isBrandOwner && vendorId) {
      dispatch(fetchSellerOrderStats(vendorId, statsOpts)).then(() =>
        setInitialLoading(false)
      );
    }
  };

  useEffect(() => {
    if (isBrandOwner && vendorId) {
      const token = getTokenFromLocalStorage();
      axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/brands`, {
          params: { vendorId },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        .then((res) => {
          const list = res.data?.data || res.data || [];
          const rows = Array.isArray(list) ? list : [];
          setBrands(
            rows
              .filter((b) => b && b._id)
              .map((b) => ({ id: String(b._id), name: b.tradingName || b.name || "Brand" }))
          );
          const first = rows[0] || null;
          if (first) {
            setBrandId(String(first._id));
            setBrandName(first.tradingName || first.name || "");
          } else {
            setInitialLoading(false);
          }
        })
        .catch(() => setInitialLoading(false));
    }
  }, [isBrandOwner, vendorId]);

  useEffect(() => {
    if (!isBrandOwner) fetchData();
  }, [dispatch, vendorId]);

  useEffect(() => {
    if (isBrandOwner) fetchData();
  }, [isBrandOwner]);

  // ── Resolve brand shop path for GA4 analytics ────────────────────────────
  useEffect(() => {
    if (!isBrandOwner) return;
    apiGetVendorShopPath()
      .then((res) => setBrandShopPath(res.data.path))
      .catch(() => {});
  }, [isBrandOwner]);

  // ── Fetch brand-scoped analytics ─────────────────────────────────────────
  useEffect(() => {
    if (!isBrandOwner || !brandShopPath) return;
    setBrandAnalyticsLoading(true);
    setBrandAnalyticsError(null);
    apiGetVendorAnalytics(brandShopPath, brandAnalyticsDays)
      .then((res) => setBrandAnalytics(res.data))
      .catch((err) => setBrandAnalyticsError(err.response?.data?.message || 'Failed to load shop analytics'))
      .finally(() => setBrandAnalyticsLoading(false));
  }, [isBrandOwner, brandShopPath, brandAnalyticsDays]);

  // ── Retailer / Wholesaler GA4 — resolve shop path ─────────────────────────
  useEffect(() => {
    if (!isRetailerWholesaler || !vendorId) return;
    apiGetVendorShopPath()
      .then((res) => setRetailerShopPath(res.data.path))
      .catch(() => {});
  }, [isRetailerWholesaler, vendorId]);

  // ── Retailer / Wholesaler GA4 — fetch historical analytics ────────────────
  useEffect(() => {
    if (!isRetailerWholesaler || !retailerShopPath) return;
    setRetailerAnalyticsLoading(true);
    apiGetVendorAnalytics(retailerShopPath, retailerAnalyticsDays)
      .then((res) => setRetailerAnalytics(res.data))
      .catch(() => {})
      .finally(() => setRetailerAnalyticsLoading(false));
  }, [isRetailerWholesaler, retailerShopPath, retailerAnalyticsDays]);

  // ── Retailer / Wholesaler GA4 — realtime, poll every 2 min ───────────────
  useEffect(() => {
    if (!isRetailerWholesaler || !vendorId) return;
    const fetchRealtime = () => {
      setRetailerRealtimeLoading(true);
      apiGetVendorRealtimeAnalytics()
        .then((res) => { setRetailerRealtimeData(res.data); setRetailerRealtimeLastUpdated(new Date()); })
        .catch(() => {})
        .finally(() => setRetailerRealtimeLoading(false));
    };
    fetchRealtime();
    const interval = setInterval(fetchRealtime, 120000);
    return () => clearInterval(interval);
  }, [isRetailerWholesaler, vendorId]);

  useEffect(() => {
    if (!vendorId) return undefined;
    // Signed in: the room gate refuses an anonymous socket.
    const newSocket = io(process.env.REACT_APP_SERVER_URL, socketAuthOptions());
    // One join for every order room (the server picks the whole business or
    // this member's branches); the old join is the fallback for an older server.
    const stopRooms = attachVendorOrderRooms(newSocket, vendorId, {
      fallback: (s) => s.emit("joinVendorRoom", vendorId),
    });

    newSocket.on("newOrder", (order) => {
      // Optional chaining because a single line without `vendorId` used to
      // throw a TypeError INSIDE the socket listener, which swallows the whole
      // alert for the order — silently, since socket.io has no error surface.
      if (
        (order?.orderItems || []).some(
          (item) => String(item?.vendorId || "") === vendorId
        )
      ) {
        fetchData();
        if (notificationsEnabledRef.current) {
          toast.info("New order received!");
          // The ring is AdminNavbar's now (same server event, every page).
        }
      }
    });

    newSocket.on("updateOrder", (order) => {
      if (
        order.orderItems.some((item) => item.vendorId.toString() === vendorId)
      ) {
        fetchData();
      }
    });

    if (isBrandOwner && !isBranchScoped) {
      newSocket.on("brandQuote:newRequest", () => {
        fetchData();
        if (notificationsEnabledRef.current) {
          toast.info("New quote request received!");
          notifyRef.current({
            title: "New quote request",
            body: "A new quote request just arrived. Tap to view.",
            url: "/admin/brand-quotes",
            tag: "new-quote",
          });
        }
      });
    }

    return () => {
      stopRooms();
      newSocket.off("newOrder");
      newSocket.off("updateOrder");
      newSocket.off("brandQuote:newRequest");
      newSocket.disconnect();
    };
  }, [vendorId, isBrandOwner, isBranchScoped]);

  // Toggle drives the shared engine (audio unlock + notification permission +
  // Web Push) in addition to this view's own on/off UI state.
  const toggleNotifications = alerts.toggle;

  if (initialLoading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <Container className={classes.root}>
      {loading && (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      )}
      {error && (
        <div>
          <Typography variant="h6" className={classes.error}>
            Error: {error}
          </Typography>
        </div>
      )}
      {!loading && !error && (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 10 }}>
            <Typography variant="h4" className={classes.dashboardTitle} style={{ margin: 0 }}>
              <AssignmentIcon style={{ fontSize: "1.75rem", color: "#013A63" }} />
              {isBrandOwner ? (brandName || "Brand") : user?.result?.businessName} Dashboard
            </Typography>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("openInHouseOrderModal"))}
              style={{ padding: "9px 18px", borderRadius: 8, background: "#f2c811", border: "none", fontWeight: 700, fontSize: 13, cursor: "pointer", color: "#0f172a", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", boxShadow: "0 2px 8px rgba(242,200,17,0.35)" }}
            >
              + New In-House Order
            </button>
          </div>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                className={classes.card}
                style={{
                  background:
                    "linear-gradient(135deg, #E1523D 0%, #c93d2a 100%)",
                  color: "#ffffff",
                  cursor: "pointer",
                }}
                {...openOrders("new", "Open new orders")}
              >
                <CardContent className={classes.cardContent}>
                  <div className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardTitle}>
                      NEW ORDER(S)
                    </Typography>
                    <RefreshIcon className={classes.icon} />
                  </div>
                  <Typography variant="h2" className={classes.statNumber}>
                    {stats.newOrders}
                  </Typography>
                </CardContent>
                <div className={classes.cardFooter}>
                  <Typography
                    variant="body2"
                    className={classes.newOrdersFooter}
                  >
                    → Manage Order(s)
                  </Typography>
                </div>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                className={classes.card}
                style={{
                  background:
                    "linear-gradient(135deg, #ED8B16 0%, #d67a0e 100%)",
                  color: "#ffffff",
                  cursor: "pointer",
                }}
                {...openOrders("pending", "Open orders pending delivery")}
              >
                <CardContent className={classes.cardContent}>
                  <div className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardTitle}>
                      PENDING DELIVERY
                    </Typography>
                    <LocalShippingIcon className={classes.icon} />
                  </div>
                  <Typography variant="h2" className={classes.statNumber}>
                    {stats.pendingDelivery}
                  </Typography>
                </CardContent>
                <div className={classes.cardFooter}>
                  <Typography
                    variant="body2"
                    className={classes.pendingDeliveryFooter}
                  >
                    → Dispatched
                  </Typography>
                </div>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                className={classes.card}
                style={{
                  background:
                    "linear-gradient(135deg, #004d40 0%, #00332b 100%)",
                  color: "#ffffff",
                  cursor: "pointer",
                }}
                {...openOrders("delivered", "Open delivered orders")}
              >
                <CardContent className={classes.cardContent}>
                  <div className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardTitle}>
                      DELIVERED
                    </Typography>
                    <CheckCircleIcon className={classes.icon} />
                  </div>
                  <Typography variant="h2" className={classes.statNumber}>
                    {stats.delivered}
                  </Typography>
                </CardContent>
                <div className={classes.cardFooter}>
                  <Typography
                    variant="body2"
                    className={classes.deliveredFooter}
                  >
                    → Overall Delivered
                  </Typography>
                </div>
              </Card>
            </Grid>
            {/* Quotes are head office's (4 Oct 2026): a brand member limited to
                some branches has no quotes to view, so no quotes card. */}
            {!(isBrandOwner && isBranchScoped) && (
            <Grid item xs={12} sm={6} md={4}>
              <Card
                className={classes.card}
                style={{
                  background:
                    "linear-gradient(135deg, #795548 0%, #5d4037 100%)",
                  color: "#ffffff",
                  cursor: "pointer",
                }}
                {...openOrders(isBrandOwner ? { tab: "quotes" } : "assigned", isBrandOwner ? "Open quote requests" : "Open assigned orders")}
              >
                <CardContent className={classes.cardContent}>
                  <div className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardTitle}>
                      {isBrandOwner ? "OPEN QUOTE REQUESTS" : "ASSIGNED ORDERS"}
                    </Typography>
                    <AssignmentIcon className={classes.icon} />
                  </div>
                  <Typography variant="h2" className={classes.statNumber}>
                    {stats.assigned}
                  </Typography>
                </CardContent>
                <div className={classes.cardFooter}>
                  <Typography
                    variant="body2"
                    className={classes.assignedOrdersFooter}
                  >
                    {isBrandOwner ? "→ View Quotes" : "→ Assigned Orders"}
                  </Typography>
                </div>
              </Card>
            </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                className={classes.card}
                style={{
                  background:
                    "linear-gradient(135deg, #607d8b 0%, #455a64 100%)",
                  color: "#ffffff",
                  cursor: "pointer",
                }}
                {...openOrders("delivered", "Open delivered orders, which the average is made from")}
              >
                <CardContent className={classes.cardContent}>
                  <div className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardTitle}>
                      AVG DELIVERY TIME
                    </Typography>
                    <TimerIcon className={classes.icon} />
                  </div>
                  <Typography variant="h2" className={classes.statNumber}>
                    {formatAvgDelivery(stats.avgDeliveryMinutes)}
                  </Typography>
                </CardContent>
                <div className={classes.cardFooter}>
                  <Typography
                    variant="body2"
                    className={classes.avgDeliveryTimeFooter}
                  >
                    → Average Delivery Time
                  </Typography>
                </div>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                className={classes.card}
                style={{
                  background:
                    "linear-gradient(135deg, #ffc107 0%, #ffa000 100%)",
                  color: "#ffffff",
                }}
              >
                <CardContent className={classes.cardContent}>
                  <div className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardTitle}>
                      PAYMENT METHODS
                    </Typography>
                    <PaymentIcon className={classes.icon} />
                  </div>
                  <div style={{ marginTop: "8px" }}>
                    {/* One chip per tender, each opening the orders paid that way. */}
                    {Object.keys(stats.paymentMethods || {}).map((method) => (
                      <span
                        key={method}
                        className={classes.paymentMethod}
                        style={{ cursor: "pointer" }}
                        {...openOrders({ method }, `Open orders paid by ${method}`)}
                      >
                        {method}: {stats.paymentMethods[method]}
                      </span>
                    ))}
                  </div>
                </CardContent>
                <div
                  className={classes.cardFooter}
                  style={{ cursor: "pointer" }}
                  {...openOrders(null, "Open all orders")}
                >
                  <Typography
                    variant="body2"
                    className={classes.paymentMethodsFooter}
                  >
                    → Payment Methods
                  </Typography>
                </div>
              </Card>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <Card
                className={classes.card}
                style={{
                  background:
                    "linear-gradient(135deg, #033B3D 0%, #022528 100%)",
                  color: "#ffffff",
                }}
              >
                <CardContent className={classes.cardContent}>
                  <div className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardTitle}>
                      NOTIFICATION SETTINGS
                    </Typography>
                    {notificationsEnabled ? (
                      <NotificationsIcon className={classes.icon} />
                    ) : (
                      <NotificationsOffIcon className={classes.icon} />
                    )}
                  </div>
                  <Button
                    variant="contained"
                    className={
                      notificationsEnabled
                        ? classes.notificationButton
                        : classes.notificationButtonOff
                    }
                    startIcon={
                      notificationsEnabled ? (
                        <NotificationsIcon />
                      ) : (
                        <NotificationsOffIcon />
                      )
                    }
                    onClick={toggleNotifications}
                  >
                    {notificationsEnabled
                      ? "Notifications On"
                      : "Notifications Off"}
                  </Button>
                  {notificationsEnabled && audioBlocked && (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={alerts.unlockAudio}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          alerts.unlockAudio();
                        }
                      }}
                      style={{
                        marginTop: 10,
                        padding: "9px 12px",
                        borderRadius: 8,
                        background: "rgba(255, 193, 7, 0.16)",
                        border: "1px solid rgba(255, 193, 7, 0.45)",
                        color: "#ffe08a",
                        fontSize: 12.5,
                        lineHeight: 1.5,
                        cursor: "pointer",
                      }}
                    >
                      Your browser is blocking the alert sound. Tap here to
                      allow it — otherwise new orders arrive silently.
                    </div>
                  )}
                </CardContent>
                <div className={classes.cardFooter}>
                  <Typography
                    variant="body2"
                    className={classes.notificationFooter}
                  >
                    → Toggle Notifications
                  </Typography>
                </div>
              </Card>
            </Grid>
          </Grid>

          {/* ── Sales Analytics (daily / weekly / monthly) ────────────────── */}
          {/* One earnings panel per brand: each brand carries its own commission
              contract, so a merged figure would hide whichever brand is weak. */}
          {isBrandOwner && !isBranchScoped &&
            brands.map((b) => (
              <VendorEarnings key={b.id} vendorId={b.id} title={`${b.name} earnings`} />
            ))}
          {vendorId && !isBrandOwner && !isBranchScoped && <VendorEarnings vendorId={vendorId} />}
          {vendorId && !isBrandOwner && <VendorSalesAnalytics vendorId={vendorId} />}

          {/* ── Chat Analytics (WhatsApp & app conversations) ─────────────── */}
          {/* Customer chats are head office's (4 Oct 2026): a member limited to
              some branches has no inbox, so no chat figures either. */}
          {!isBranchScoped && <ChatAnalytics vendorId={vendorId} />}

          {/* ── Brand Shop Analytics (SEO / GA4) ─────────────────────────── */}
          {isBrandOwner && (
            <div style={{ marginTop: 24 }}>
              {/* Header bar */}
              <Card className={classes.card} style={{ background: "linear-gradient(135deg, #0a578b 0%, #063d62 100%)", color: "#fff", marginBottom: 16 }}>
                <CardContent style={{ padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                  <div>
                    <Typography style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.65)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 2 }}>
                      <i className="fas fa-chart-line" style={{ marginRight: 6 }}></i> Shop Performance · lusuku.ug
                    </Typography>
                    <Typography style={{ fontWeight: 700, fontSize: "1.05rem" }}>
                      Brand Shop Analytics
                    </Typography>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    {[7, 14, 30].map((d) => (
                      <button key={d} onClick={() => setBrandAnalyticsDays(d)} style={{
                        padding: "5px 13px", borderRadius: 20, border: "none", cursor: "pointer",
                        fontSize: "0.72rem", fontWeight: 700, transition: "all 0.15s",
                        background: brandAnalyticsDays === d ? "#fff" : "rgba(255,255,255,0.2)",
                        color: brandAnalyticsDays === d ? "#0a578b" : "#fff",
                      }}>{d}d</button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Loading */}
              {brandAnalyticsLoading && (
                <div style={{ textAlign: "center", padding: "32px 0" }}>
                  <CircularProgress size={28} style={{ color: "#0a578b" }} />
                  <Typography variant="body2" style={{ color: "#64748b", marginTop: 8, fontSize: "0.85rem" }}>Loading shop analytics…</Typography>
                </div>
              )}

              {/* Error */}
              {brandAnalyticsError && (
                <Card style={{ borderRadius: 12, background: "#fff3cd", border: "1px solid #ffc107", marginBottom: 16 }}>
                  <CardContent style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 10, fontSize: "0.85rem", color: "#856404" }}>
                    <i className="fas fa-exclamation-triangle"></i>
                    <span>{brandAnalyticsError}</span>
                    <span onClick={() => setBrandAnalyticsDays((p) => p)} style={{ marginLeft: "auto", cursor: "pointer", textDecoration: "underline" }}>Retry</span>
                  </CardContent>
                </Card>
              )}

              {/* KPI cards */}
              {brandAnalytics && (() => {
                const s = brandAnalytics.summary;
                const accent = "#0a578b";
                const bounceOk = Number(s.bounceRate) < 0.5;
                const kpis = [
                  { label: "Shop Visitors",  value: Number(s.totalUsers).toLocaleString(),                                                                      icon: "fas fa-users",         border: "#0a578b" },
                  { label: "Page Views",     value: Number(s.pageViews).toLocaleString(),                                                                        icon: "fas fa-eye",           border: "#1a56db" },
                  { label: "Sessions",       value: Number(s.sessions).toLocaleString(),                                                                         icon: "fas fa-mouse-pointer", border: "#0694a2" },
                  { label: "Bounce Rate",    value: `${(Number(s.bounceRate) * 100).toFixed(1)}%`,                                                               icon: "fas fa-sign-out-alt",  border: bounceOk ? "#057a55" : "#c81e1e" },
                  { label: "Avg Time",       value: `${Math.floor(Number(s.avgSessionDuration) / 60)}m ${Math.round(Number(s.avgSessionDuration) % 60)}s`,       icon: "fas fa-clock",         border: "#7e3af2" },
                ];
                return (
                  <>
                    <Grid container spacing={2}>
                      {kpis.map(({ label, value, icon, border }) => (
                        <Grid item xs={6} sm={4} md key={label}>
                          <Card style={{ borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.08)", borderLeft: `4px solid ${border}`, background: "#fff" }}>
                            <CardContent style={{ padding: "14px 16px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                                <Typography style={{ fontSize: "0.7rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px", fontWeight: 600 }}>{label}</Typography>
                                <i className={icon} style={{ fontSize: "0.9rem", color: border }}></i>
                              </div>
                              <Typography style={{ fontWeight: 700, fontSize: "1.5rem", color: "#1e293b" }}>{value}</Typography>
                              <Typography style={{ fontSize: "0.68rem", color: "#94a3b8", marginTop: 4 }}>Last {brandAnalyticsDays} days</Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Top pages + Devices */}
                    <Grid container spacing={2} style={{ marginTop: 8 }}>
                      <Grid item xs={12} md={7}>
                        <Card style={{ borderRadius: 12, boxShadow: "0 4px 15px rgba(0,0,0,0.1)", height: "100%" }}>
                          <CardContent>
                            <Typography style={{ fontWeight: 700, fontSize: "0.95rem", marginBottom: 4 }}>Top Shop Pages</Typography>
                            <Typography variant="body2" style={{ color: "#64748b", fontSize: "0.78rem", marginBottom: 12 }}>Most viewed pages inside your brand shop</Typography>
                            {(brandAnalytics.topPages || []).length === 0 ? (
                              <Typography variant="body2" style={{ color: "#94a3b8", textAlign: "center", padding: "20px 0" }}>No page view data yet.</Typography>
                            ) : (() => {
                              const pages = brandAnalytics.topPages.slice(0, 5);
                              const maxV = Math.max(...pages.map((p) => Number(p.screenPageViews)), 1);
                              const colors = [accent, "#1a56db", "#0694a2", "#7e3af2", "#057a55"];
                              return pages.map((page, i) => (
                                <div key={i} style={{ marginBottom: 12 }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                                    <span style={{ fontSize: "0.78rem", color: "#525f7f", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "72%" }} title={page.pagePath}>
                                      <i className="fas fa-file-alt" style={{ color: colors[i] || accent, fontSize: "0.7rem", marginRight: 4 }}></i>
                                      {page.pagePath || "/"}
                                    </span>
                                    <span style={{ fontSize: "0.75rem", fontWeight: 700, color: colors[i] || accent }}>{Number(page.screenPageViews).toLocaleString()}</span>
                                  </div>
                                  <div style={{ background: "#f1f3f9", borderRadius: 4, height: 6, overflow: "hidden" }}>
                                    <div style={{ width: `${(Number(page.screenPageViews) / maxV) * 100}%`, height: "100%", background: colors[i] || accent, borderRadius: 4, transition: "width 0.6s ease" }} />
                                  </div>
                                </div>
                              ));
                            })()}
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <Card style={{ borderRadius: 12, boxShadow: "0 4px 15px rgba(0,0,0,0.1)", height: "100%" }}>
                          <CardContent>
                            <Typography style={{ fontWeight: 700, fontSize: "0.95rem", marginBottom: 4 }}>
                              <i className="fas fa-mobile-alt" style={{ color: accent, marginRight: 6 }}></i>How Customers Browse
                            </Typography>
                            <Typography variant="body2" style={{ color: "#64748b", fontSize: "0.78rem", marginBottom: 12 }}>Device types visiting your shop</Typography>
                            {(brandAnalytics.devices || []).length === 0 ? (
                              <Typography variant="body2" style={{ color: "#94a3b8", textAlign: "center", padding: "20px 0" }}>No device data yet.</Typography>
                            ) : (() => {
                              const devices = brandAnalytics.devices;
                              const total = devices.reduce((sum, x) => sum + Number(x.sessions), 0) || 1;
                              const devColors = { desktop: "#1a56db", mobile: accent, tablet: "#057a55" };
                              const devIcons = { desktop: "fas fa-desktop", mobile: "fas fa-mobile-alt", tablet: "fas fa-tablet-alt" };
                              return devices.map((d, i) => {
                                const pct = (Number(d.sessions) / total) * 100;
                                const clr = devColors[d.deviceCategory] || ["#7e3af2", "#0694a2", "#c81e1e"][i] || "#adb5bd";
                                const ico = devIcons[d.deviceCategory] || "fas fa-question";
                                return (
                                  <div key={i} style={{ marginBottom: 14 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                                      <span style={{ fontSize: "0.8rem", color: "#525f7f", textTransform: "capitalize", display: "flex", alignItems: "center", gap: 6 }}>
                                        <i className={ico} style={{ color: clr, width: 14, textAlign: "center" }}></i>{d.deviceCategory}
                                      </span>
                                      <span style={{ fontSize: "0.78rem", fontWeight: 700, color: clr }}>{pct.toFixed(1)}%</span>
                                    </div>
                                    <div style={{ background: "#f1f3f9", borderRadius: 20, height: 10, overflow: "hidden" }}>
                                      <div style={{ width: `${pct}%`, height: "100%", background: clr, borderRadius: 20, transition: "width 0.6s ease" }} />
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </>
                );
              })()}

              {/* Empty state */}
              {!brandAnalyticsLoading && !brandAnalyticsError && !brandAnalytics && brandShopPath && (
                <Card style={{ borderRadius: 12, boxShadow: "0 4px 15px rgba(0,0,0,0.08)" }}>
                  <CardContent style={{ textAlign: "center", padding: "40px 20px" }}>
                    <i className="fas fa-satellite-dish" style={{ fontSize: "2.5rem", color: "#adb5bd", display: "block", marginBottom: 12 }}></i>
                    <Typography variant="body2" style={{ color: "#64748b" }}>No shop data yet — your brand shop page needs visitors to appear here.</Typography>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* ── Retailer / Wholesaler Shop Analytics (GA4) ─────────────────── */}
          {isRetailerWholesaler && retailerShopPath && (
            <div style={{ marginTop: 24, background: '#0d1117', borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)' }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ background: 'linear-gradient(135deg,#e3a008,#f59e0b)', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fas fa-chart-line" style={{ color: '#141824', fontSize: '0.9rem' }} />
                  </div>
                  <div>
                    <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.92rem' }}>
                      <i className="fas fa-chart-line" style={{ color: '#f59e0b', marginRight: 6 }} />
                      Shop Analytics
                    </div>
                    <div style={{ color: '#6b7a99', fontSize: '0.72rem' }}>
                      Google Analytics 4 · lusuku.ug · {retailerAnalyticsTab === 'realtime' ? 'Live · polls every 2 min' : `Last ${retailerAnalyticsDays} days`}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {/* Tab switcher */}
                  <div style={{ display: 'flex', gap: 2, background: '#1a1f2e', borderRadius: 8, padding: 3 }}>
                    {[['overview', 'Overview'], ['realtime', 'Realtime']].map(([key, label]) => (
                      <button key={key} onClick={() => setRetailerAnalyticsTab(key)} style={{
                        padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                        fontSize: '0.75rem', fontWeight: 700, transition: 'all 0.15s',
                        background: retailerAnalyticsTab === key
                          ? key === 'realtime' ? 'linear-gradient(135deg,#10b981,#059669)' : 'linear-gradient(135deg,#e3a008,#f59e0b)'
                          : 'transparent',
                        color: retailerAnalyticsTab === key ? (key === 'realtime' ? '#fff' : '#141824') : '#6b7a99',
                        display: 'flex', alignItems: 'center', gap: 5,
                      }}>
                        {key === 'realtime' && (
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: retailerAnalyticsTab === 'realtime' ? '#fff' : '#10b981', display: 'inline-block', flexShrink: 0 }} />
                        )}
                        {label}
                      </button>
                    ))}
                  </div>
                  {/* Day range — overview only */}
                  {retailerAnalyticsTab === 'overview' && (
                    <div style={{ display: 'flex', gap: 2, background: '#1a1f2e', borderRadius: 8, padding: 3 }}>
                      {[7, 14, 30].map((d) => (
                        <button key={d} onClick={() => setRetailerAnalyticsDays(d)} style={{
                          padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                          fontSize: '0.72rem', fontWeight: 700, transition: 'all 0.15s',
                          background: retailerAnalyticsDays === d ? 'linear-gradient(135deg,#e3a008,#f59e0b)' : 'transparent',
                          color: retailerAnalyticsDays === d ? '#141824' : '#6b7a99',
                        }}>{d}d</button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: 20 }}>

                {/* ── Overview tab ── */}
                {retailerAnalyticsTab === 'overview' && (
                  retailerAnalyticsLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                      <CircularProgress size={20} style={{ color: '#f59e0b' }} />
                      <p style={{ marginTop: 12, color: '#6b7a99', fontSize: '0.82rem' }}>Loading analytics…</p>
                    </div>
                  ) : retailerAnalytics ? (() => {
                    const s = retailerAnalytics.summary;
                    const kpis = [
                      { label: 'Shop Visitors', value: Number(s.totalUsers).toLocaleString(),   sub: `Last ${retailerAnalyticsDays} days`, icon: 'fas fa-users',          color: '#e67e22' },
                      { label: 'Page Views',    value: Number(s.pageViews).toLocaleString(),    sub: `${(Number(s.pageViews)/Math.max(Number(s.sessions),1)).toFixed(1)} per visit`, icon: 'fas fa-eye', color: '#1a56db' },
                      { label: 'Sessions',      value: Number(s.sessions).toLocaleString(),     sub: 'total visits',                        icon: 'fas fa-mouse-pointer',  color: '#0694a2' },
                      { label: 'Avg Time',      value: `${Math.floor(Number(s.avgSessionDuration)/60)}m ${Math.round(Number(s.avgSessionDuration)%60)}s`, sub: 'per visit', icon: 'fas fa-stopwatch', color: '#7e3af2' },
                      { label: 'Bounce Rate',   value: `${(Number(s.bounceRate)*100).toFixed(1)}%`, sub: Number(s.bounceRate) < 0.5 ? 'Healthy' : 'High', icon: 'fas fa-sign-out-alt', color: Number(s.bounceRate) < 0.5 ? '#057a55' : '#c81e1e' },
                    ];
                    return (
                      <>
                        <Grid container spacing={2} style={{ marginBottom: 16 }}>
                          {kpis.map((k) => (
                            <Grid item xs={6} sm={4} md key={k.label}>
                              <div style={{ background: '#141824', borderRadius: 12, padding: '14px 16px', height: '100%' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                                  <i className={k.icon} style={{ color: k.color, fontSize: '0.9rem' }} />
                                  <span style={{ color: '#6b7a99', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 600 }}>{k.label}</span>
                                </div>
                                <div style={{ color: '#fff', fontSize: '1.4rem', fontWeight: 700, lineHeight: 1 }}>{k.value}</div>
                                <div style={{ color: '#4b5563', fontSize: '0.68rem', marginTop: 4 }}>{k.sub}</div>
                              </div>
                            </Grid>
                          ))}
                        </Grid>
                        {(retailerAnalytics.topPages || []).length > 0 && (
                          <div style={{ background: '#141824', borderRadius: 12, padding: '14px 18px', marginBottom: 12 }}>
                            <div style={{ color: '#c9d3ec', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 12 }}>
                              <i className="fas fa-fire" style={{ color: '#f43f5e', marginRight: 6 }} />Top Pages
                            </div>
                            {retailerAnalytics.topPages.slice(0, 5).map((page, i) => {
                              const views = Number(page.screenPageViews);
                              const maxV = Math.max(...retailerAnalytics.topPages.slice(0,5).map(p => Number(p.screenPageViews)), 1);
                              const colors = ['#10b981','#3b82f6','#f59e0b','#8b5cf6','#06b6d4'];
                              return (
                                <div key={i} style={{ marginBottom: 10 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span style={{ fontSize: '0.74rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '78%' }}>{page.pagePath || '/'}</span>
                                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: colors[i], whiteSpace: 'nowrap', marginLeft: 6 }}>{views.toLocaleString()} views</span>
                                  </div>
                                  <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 5, overflow: 'hidden' }}>
                                    <div style={{ width: `${(views/maxV)*100}%`, height: '100%', background: colors[i], borderRadius: 4 }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {(retailerAnalytics.devices || []).length > 0 && (
                          <div style={{ background: '#141824', borderRadius: 12, padding: '14px 18px' }}>
                            <div style={{ color: '#c9d3ec', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 12 }}>
                              <i className="fas fa-mobile-alt" style={{ color: '#0694a2', marginRight: 6 }} />Device Split
                            </div>
                            {(() => {
                              const devices = retailerAnalytics.devices;
                              const total = devices.reduce((sum, d) => sum + Number(d.sessions), 0) || 1;
                              return devices.map((d, i) => {
                                const pct = ((Number(d.sessions) / total) * 100).toFixed(1);
                                const colors = { mobile: '#10b981', desktop: '#3b82f6', tablet: '#f59e0b' };
                                const clr = colors[d.deviceCategory?.toLowerCase()] || '#8b5cf6';
                                return (
                                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                    <span style={{ width: 70, fontSize: '0.72rem', color: '#94a3b8', textTransform: 'capitalize' }}>{d.deviceCategory}</span>
                                    <div style={{ flex: 1, background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                                      <div style={{ width: `${pct}%`, height: '100%', background: clr, borderRadius: 4 }} />
                                    </div>
                                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: clr, width: 38, textAlign: 'right' }}>{pct}%</span>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        )}
                      </>
                    );
                  })() : (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b7a99', fontSize: '0.84rem' }}>
                      No analytics data yet for your shop page.
                    </div>
                  )
                )}

                {/* ── Realtime tab ── */}
                {retailerAnalyticsTab === 'realtime' && (
                  retailerRealtimeLoading && !retailerRealtimeData ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                      <CircularProgress size={20} style={{ color: '#10b981' }} />
                      <p style={{ marginTop: 12, color: '#6b7a99', fontSize: '0.82rem' }}>Connecting to live data…</p>
                    </div>
                  ) : retailerRealtimeData ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
                        <div style={{ background: 'linear-gradient(135deg,#10b981,#059669)', borderRadius: 16, padding: '18px 28px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 120, boxShadow: '0 4px 20px rgba(16,185,129,0.35)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', display: 'inline-block' }} />
                            <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px' }}>Active Now</span>
                          </div>
                          <div style={{ fontSize: '2.8rem', fontWeight: 800, color: '#fff', lineHeight: 1 }}>{retailerRealtimeData.activeUsers}</div>
                          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', marginTop: 4 }}>on your shop</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                          {retailerRealtimeLoading && <CircularProgress size={12} style={{ color: '#10b981' }} />}
                          {retailerRealtimeLastUpdated && (
                            <span style={{ color: '#6b7a99', fontSize: '0.7rem' }}>
                              <i className="fas fa-clock" style={{ marginRight: 4 }} />
                              Updated {retailerRealtimeLastUpdated.toLocaleTimeString()}
                            </span>
                          )}
                          <span style={{ color: '#374151', fontSize: '0.65rem' }}>Polls every 2 min</span>
                        </div>
                      </div>
                      {(retailerRealtimeData.activePages || []).length > 0 && (
                        <div style={{ background: '#141824', borderRadius: 12, padding: '14px 18px' }}>
                          <div style={{ color: '#c9d3ec', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10 }}>
                            <i className="fas fa-map-marker-alt" style={{ color: '#10b981', marginRight: 6 }} />Active Pages
                          </div>
                          {retailerRealtimeData.activePages.map((p, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                              <span style={{ fontSize: '0.74rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>{p.unifiedScreenName || p.pagePath || '—'}</span>
                              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#10b981' }}>{p.activeUsers} active</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b7a99', fontSize: '0.84rem' }}>
                      No live visitors on your shop right now.
                    </div>
                  )
                )}

              </div>
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default SellerDashboard;
